import { useCallback, useState } from "react";

/**
 * 1 枚のパネルへ与える下限の上限 (%)。2 枚が下限を取り合っても合計が 100% を
 * 超えないようにする。3 枚以上のグループへ使う場合はこの値を見直すこと。
 */
const MAX_MIN_SIZE_PERCENT = 50;

/**
 * px 指定の下限を react-resizable-panels の minSize (%) へ換算する。
 * グループの実寸が未測定 (0) の初回描画では fallbackPercent をそのまま使う。
 */
export function toMinSizePercent(
	pixels: number,
	groupSize: number,
	fallbackPercent: number,
): number {
	if (groupSize <= 0) return fallbackPercent;
	return Math.min((pixels / groupSize) * 100, MAX_MIN_SIZE_PERCENT);
}

/**
 * PanelGroup を包む要素の実寸 (横分割なら幅、縦分割なら高さ) を測る。
 * react-resizable-panels は下限を % でしか受け取らず、% のままではディスプレイの
 * 大きさで下限の実寸が変わってしまうため、換算元の実寸をここで測る。
 */
export function usePanelGroupSize(direction: "horizontal" | "vertical") {
	const [size, setSize] = useState(0);

	const ref = useCallback(
		(node: HTMLElement | null) => {
			if (node === null) return undefined;
			const measure = () =>
				setSize(direction === "horizontal" ? node.clientWidth : node.clientHeight);
			measure();
			const observer = new ResizeObserver(measure);
			observer.observe(node);
			return () => observer.disconnect();
		},
		[direction],
	);

	return { ref, size };
}
